import type { AnalyticsConsent } from './analytics-consent'

export interface CrossDomainPostHogBootstrap {
  distinctID: string
  sessionID: string
  isIdentifiedID: false
}

export interface CrossDomainPostHogHandoff {
  bootstrap: CrossDomainPostHogBootstrap
  sourceSite: string
}

interface ReadHandoffOptions {
  now?: number
  referrer?: string
}

const DISTINCT_ID_KEY = 'sw_ph_distinct_id'
const SESSION_ID_KEY = 'sw_ph_session_id'
const FRAGMENT_ENVELOPE_KEY = 'sw_handoff'
const LEGACY_QUERY_HANDOFF_KEYS = [
  'journey_id',
  'sw_journey_id',
  'sw_source_path',
  'sw_cta_id',
  'sw_cta_location',
  'sw_entry_type',
  'sw_entry_source',
  'sw_entry_medium',
  'sw_entry_campaign',
  'sw_entry_content',
  'sw_entry_term',
  'sw_entry_referrer_domain',
  'sw_entry_landing_host',
  'sw_entry_landing_path',
] as const
const ANONYMOUS_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[47][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SESSION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SOURCE_SITE_PATTERN = /^[a-z0-9][a-z0-9.-]{0,252}$/
const ANALYTICS_SURFACE_HOSTS = new Set([
  'app.spatius.ai',
  'spatius.ai',
  'www.spatius.ai',
  'spatialwalk.cn',
  'www.spatialwalk.cn',
])
const ANALYTICS_ROOT_DOMAINS = ['spatius.ai', 'spatialwalk.cn'] as const
const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000
const SESSION_FUTURE_TOLERANCE_MS = 5 * 60 * 1000

let pendingBootstrap: CrossDomainPostHogBootstrap | undefined
let hasSnapshottedCurrentDocument = false

function canonicalUuid(value: string | null, pattern: RegExp): string | undefined {
  return value && value === value.trim() && pattern.test(value)
    ? value
    : undefined
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

export function isTrustedAnalyticsSurfaceHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/\.$/, '')
  return ANALYTICS_SURFACE_HOSTS.has(normalized) || isLoopbackHostname(normalized)
}

export function sharesAnalyticsRootDomain(targetHostname: string, currentHostname: string): boolean {
  const rootDomain = (hostname: string) => ANALYTICS_ROOT_DOMAINS
    .find(domain => hostname === domain || hostname.endsWith(`.${domain}`))
  const targetRoot = rootDomain(targetHostname)
  return Boolean(targetRoot && targetRoot === rootDomain(currentHostname))
}

function isTrustedAnalyticsSurfacePair(currentHostname: string, sourceHostname: string): boolean {
  const currentIsStudio = currentHostname === 'app.spatius.ai' || isLoopbackHostname(currentHostname)
  const sourceIsOwned = isTrustedAnalyticsSurfaceHostname(sourceHostname)
  return currentIsStudio && sourceIsOwned
}

function referrerHostname(rawReferrer: string | undefined): string | undefined {
  if (!rawReferrer)
    return undefined
  try {
    const referrer = new URL(rawReferrer)
    return ['http:', 'https:'].includes(referrer.protocol)
      ? referrer.hostname.toLowerCase().replace(/\.$/, '')
      : undefined
  }
  catch {
    return undefined
  }
}

function hasReasonableSessionTime(sessionId: string, now: number): boolean {
  const timestamp = Number.parseInt(sessionId.replaceAll('-', '').slice(0, 12), 16)
  if (!Number.isSafeInteger(timestamp) || timestamp <= 0 || !Number.isFinite(now))
    return false
  const age = now - timestamp
  return age >= -SESSION_FUTURE_TOLERANCE_MS && age <= SESSION_MAX_AGE_MS
}

function fragmentIdentityParams(url: URL): URLSearchParams | undefined {
  const raw = url.hash.slice(1)
  if (!raw)
    return undefined
  const fragment = new URLSearchParams(raw)
  if (fragment.has(FRAGMENT_ENVELOPE_KEY))
    return new URLSearchParams(fragment.get(FRAGMENT_ENVELOPE_KEY) ?? '')
  if (fragment.has(DISTINCT_ID_KEY) || fragment.has(SESSION_ID_KEY))
    return fragment
  return undefined
}

export function readCrossDomainPostHogHandoff(
  rawUrl: string,
  options: ReadHandoffOptions = {},
): CrossDomainPostHogHandoff | undefined {
  let url: URL
  try {
    url = new URL(rawUrl, 'https://app.spatius.ai')
  }
  catch {
    return undefined
  }

  const identityParams = fragmentIdentityParams(url) ?? url.searchParams
  const distinctID = canonicalUuid(identityParams.get(DISTINCT_ID_KEY), ANONYMOUS_ID_PATTERN)
  const sessionID = canonicalUuid(identityParams.get(SESSION_ID_KEY), SESSION_ID_PATTERN)
  const sourceSite = url.searchParams.get('sw_source_site')?.trim().toLowerCase().replace(/\.$/, '')
  const referrer = referrerHostname(options.referrer)
  const currentHostname = url.hostname.toLowerCase().replace(/\.$/, '')

  if (
    !distinctID
    || !sessionID
    || !sourceSite
    || !SOURCE_SITE_PATTERN.test(sourceSite)
    || referrer !== sourceSite
    || !isTrustedAnalyticsSurfacePair(currentHostname, sourceSite)
    || !hasReasonableSessionTime(sessionID, options.now ?? Date.now())
  ) {
    return undefined
  }

  return {
    bootstrap: { distinctID, sessionID, isIdentifiedID: false },
    sourceSite,
  }
}

function stripBridgeFromCurrentUrl(): void {
  if (typeof window === 'undefined')
    return
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete(DISTINCT_ID_KEY)
    url.searchParams.delete(SESSION_ID_KEY)
    url.searchParams.delete('sw_source_site')
    for (const key of LEGACY_QUERY_HANDOFF_KEYS)
      url.searchParams.delete(key)
    const segments = url.hash.slice(1).split('&').filter(Boolean)
    const retained = segments.filter((segment) => {
      const rawKey = segment.split('=', 1)[0]
      try {
        const key = decodeURIComponent(rawKey.replace(/\+/g, ' '))
        return key !== FRAGMENT_ENVELOPE_KEY && key !== DISTINCT_ID_KEY && key !== SESSION_ID_KEY
      }
      catch {
        return true
      }
    })
    const hash = retained.length ? `#${retained.join('&')}` : ''
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${hash}`)
  }
  catch {
    // URL cleanup is best-effort and must never block Studio startup.
  }
}

export function snapshotCrossDomainPostHogBootstrap(consent: AnalyticsConsent): void {
  if (hasSnapshottedCurrentDocument || typeof window === 'undefined')
    return
  hasSnapshottedCurrentDocument = true
  const handoff = readCrossDomainPostHogHandoff(window.location.href, {
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
  })
  stripBridgeFromCurrentUrl()
  pendingBootstrap = consent === false ? undefined : handoff?.bootstrap
}

export function peekCrossDomainPostHogBootstrap(consent: AnalyticsConsent, now = Date.now()): CrossDomainPostHogBootstrap | undefined {
  if (consent !== true || !pendingBootstrap)
    return undefined
  if (!hasReasonableSessionTime(pendingBootstrap.sessionID, now)) {
    pendingBootstrap = undefined
    return undefined
  }
  return pendingBootstrap
}

export function commitCrossDomainPostHogBootstrap(bootstrap: CrossDomainPostHogBootstrap): void {
  if (pendingBootstrap === bootstrap)
    pendingBootstrap = undefined
}

export function clearCrossDomainPostHogBootstrap(): void {
  pendingBootstrap = undefined
}
